import get from 'lodash/get';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { DepositFrequencyType } from '../../../../api/useAchFundingOptionsQuery';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Label } from '../../../../../../CommonComponents/OPL/Label/Label';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { SingleDatePicker } from '../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { HelperText } from '../../../../../../CommonComponents/OPL/HelperText/HelperText';
import { isConsumerArea as selectIsConsumerArea } from '../../../../../../CommonSelectors/CommonAreaSelector';
import { LinkAccountsModal } from '../../../ManageAccounts/components/LinkAccountsModal/LinkAccountsModal';
import { DepositMetadata, useDepositForm } from './useDepositForm';
import { DepositParams } from '../../../../api/useDepositMutation';

interface Props {
  onClose(): void;
  onBack?(): void;
  onSuccess({ params, metadata }: { params: DepositParams; metadata: DepositMetadata }): void;
  isStandalone?: boolean;
}

export const DepositForm: React.FC<Props> = ({ onBack, onClose, onSuccess, isStandalone }) => {
  const { data, actions } = useDepositForm({ onSuccess });

  const [isLinkAccountsOpen, setIsLinkAccountsOpen] = useState(false);

  const isConsumerArea = useSelector(selectIsConsumerArea);

  if (!data) {
    return null;
  }

  const {
    asyncData: {
      accountOptions,
      contributionTypesOptions,
      contributionYearsOptions,
      isIraAccount,
      fundingFrequencyTypesOptions,
    },
    formBag: {
      handleSubmit,
      control,
      setValue,
      watch,
      formState: { errors, isSubmitting: isDepositing },
    },
    depositImpediment,
  } = data;

  const { onSubmit } = actions;

  useEffect(() => {
    // Select first account by default
    if (accountOptions && isStandalone) {
      setValue('account', accountOptions[0]);
    }
  }, [accountOptions, isStandalone, setValue]);

  const currentScheduledFundingFrequency = watch('scheduledFundingFrequency');

  return (
    <>
      <Box
        as="form"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        mt="16"
        onSubmit={handleSubmit(onSubmit)}
      >
        <ModalBody>
          {depositImpediment && (
            <Alert message={depositImpediment.message} variant={depositImpediment.level} mb="16" />
          )}

          <Controller
            control={control}
            name="account"
            render={({ field: { value, onChange, ref } }) => (
              <Select
                innerRef={ref}
                label="Account"
                options={accountOptions}
                value={value}
                placeholder="Account"
                onChange={onChange}
                formVariant={get(errors, 'account') ? 'error' : undefined}
                helperText={get(errors, 'account.message')}
              />
            )}
          />

          <Controller
            control={control}
            name="amount"
            render={({ field: { value, onChange, ref } }) => (
              <InputNumberMask
                ref={ref}
                label="Amount"
                placeholder="Amount"
                leftIcon="fa fa-dollar-sign"
                name="amount"
                value={value}
                formVariant={errors.amount ? 'error' : undefined}
                helperText={errors.amount?.message}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue);
                }}
                mt="16"
              />
            )}
          />

          {isIraAccount && contributionTypesOptions !== null && contributionYearsOptions !== null && (
            <Box display="flex" mt="16">
              <Box mr="8" width="50%">
                <Controller
                  control={control}
                  name="contributionTypesOptions"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label="Contribution Type"
                      options={contributionTypesOptions}
                      value={value}
                      placeholder="Contribution Type"
                      isPortal
                      onChange={onChange}
                      formVariant={get(errors, 'contributionTypesOptions') ? 'error' : undefined}
                      helperText={get(errors, 'contributionTypesOptions.message')}
                    />
                  )}
                />
              </Box>
              <Box ml="8" width="50%">
                <Controller
                  control={control}
                  name="contributionYearsOptions"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      disabled={contributionYearsOptions.length === 0}
                      label="Contribution Year"
                      options={contributionYearsOptions}
                      value={value}
                      placeholder="Contribution Year"
                      isPortal
                      onChange={onChange}
                      formVariant={get(errors, 'contributionYearsOptions') ? 'error' : undefined}
                      helperText={get(errors, 'contributionYearsOptions.message')}
                    />
                  )}
                />
              </Box>
            </Box>
          )}

          <Box width="50%" pr="8">
            <Controller
              control={control}
              name="scheduledFundingFrequency"
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Frequency"
                  options={fundingFrequencyTypesOptions}
                  value={value}
                  placeholder="Select an item..."
                  onChange={onChange}
                  formVariant={get(errors, 'scheduledFundingFrequency') ? 'error' : undefined}
                  helperText={get(errors, 'scheduledFundingFrequency.message')}
                  isPortal
                  mt="16"
                />
              )}
            />
            {currentScheduledFundingFrequency && (
              <Controller
                control={control}
                name="scheduledFirstDepositDate"
                render={({ field: { value, onChange } }) => (
                  <>
                    <Label mt="16" mb="4" css="display: block">
                      {`Date of ${
                        currentScheduledFundingFrequency.value === DepositFrequencyType.OneTime
                          ? ''
                          : 'First '
                      }Deposit`}
                    </Label>
                    <SingleDatePicker
                      defaultView="month"
                      date={value}
                      onChange={onChange}
                      minDate={new Date()}
                      maxDate={
                        currentScheduledFundingFrequency?.showSecondRecurringDepositDay
                          ? moment()
                              .add(1, 'months')
                              .toDate()
                          : undefined
                      }
                      isWrapperWidth
                    />
                  </>
                )}
              />
            )}
            {currentScheduledFundingFrequency?.showSecondRecurringDepositDay && (
              <Controller
                control={control}
                name="scheduledSecondDepositDate"
                render={({ field: { value, onChange } }) => (
                  <>
                    <Label
                      mt="16"
                      mb="4"
                      css="display: block"
                      variant={get(errors, 'scheduledSecondDepositDate') ? 'error' : undefined}
                    >
                      Date of Second Deposit
                    </Label>
                    <SingleDatePicker
                      defaultView="month"
                      date={value}
                      onChange={onChange}
                      minDate={new Date()}
                      maxDate={moment()
                        .add(1, 'months')
                        .toDate()}
                      isWrapperWidth
                    />
                    {get(errors, 'scheduledSecondDepositDate') ? (
                      <HelperText variant="error" mt="4">
                        {get(errors, 'scheduledSecondDepositDate.message')}
                      </HelperText>
                    ) : null}
                  </>
                )}
              />
            )}
          </Box>
        </ModalBody>

        {isStandalone && isConsumerArea && (
          <Button
            variant="subtleActive"
            leftIcon="fas fa-plus"
            onClick={() => setIsLinkAccountsOpen(true)}
            ml={12}
            css="align-self: flex-start"
          >
            Link New Account
          </Button>
        )}

        <ModalFooter>
          <Box flex={1}>
            {onBack && (
              <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
                Back
              </Button>
            )}
          </Box>

          <Button variant="subtleInactive" onClick={onClose} mr="8">
            Cancel
          </Button>

          <Button type="submit" isLoading={isDepositing}>
            Continue
          </Button>
        </ModalFooter>
      </Box>

      {isLinkAccountsOpen && <LinkAccountsModal onClose={() => setIsLinkAccountsOpen(false)} />}
    </>
  );
};
